import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getOrganizations } from '@/lib/firebase/queries/organizations';
import type { Organization } from '@/types/organization';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Users, Bot, MessageCircle, Play, CheckCircle, XCircle, Clock, User, X, Code, Copy, Phone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { HabitPresetManagement } from './HabitPresetManagement';

interface HabitSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HabitSettingsDialog({ open, onOpenChange }: HabitSettingsDialogProps) {
  const { t, dir, language } = useLanguage();
  const isRTL = language === 'ar';
  
  // AI Follow-up settings state
  const [aiFollowup, setAiFollowup] = useState({
    enabled: false,
    followupTime: '21:00',
    dailyScheduleEnabled: false,
    organizationSpecificEnabled: false,
    selectedOrganizations: [] as string[],
  });
  const [aiFollowupLoading, setAiFollowupLoading] = useState(false);
  
  // Organizations data
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  
  // AI Testing state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [testConversations, setTestConversations] = useState<any[]>([]);
  const [deletingConversations, setDeletingConversations] = useState<Set<string>>(new Set());
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Debug modal state
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);

  useEffect(() => {
    if (open) {
      fetchAiFollowupSettings();
      fetchOrganizations();
      loadTestConversations();
      
      // Set up real-time listener for test conversations
      const today = new Date().toISOString().split('T')[0];
      const conversationsQuery = query(
        collection(db, 'habitFollowupConversations'),
        where('date', '==', today),
        where('isTest', '==', true)
      );
      
      const unsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
        const conversations = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTestConversations(conversations);
      });
      
      unsubscribeRef.current = unsubscribe;
    }
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [open]);

  const hours = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });

  const fetchOrganizations = async () => {
    try {
      setOrganizationsLoading(true);
      const orgsData = await getOrganizations();
      setOrganizations(orgsData);
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const fetchAiFollowupSettings = async () => {
    try {
      const aiSettingsDoc = await getDoc(doc(db, 'settings', 'aiFollowup'));
      let aiData = { enabled: false };

      if (aiSettingsDoc.exists()) {
        aiData = aiSettingsDoc.data();
      }

      const dailyScheduleDoc = await getDoc(doc(db, 'settings', 'dailySchedule'));
      let scheduleData = { enabled: false, followupTime: '21:00' };
      
      if (dailyScheduleDoc.exists()) {
        scheduleData = dailyScheduleDoc.data();
      }

      const orgSpecificDoc = await getDoc(doc(db, 'settings', 'organizationSpecificFollowup'));
      let orgSpecificData = { enabled: false, selectedOrganizations: [] };
      
      if (orgSpecificDoc.exists()) {
        orgSpecificData = orgSpecificDoc.data();
      }

      setAiFollowup({
        enabled: aiData.enabled || false,
        followupTime: scheduleData.followupTime || '21:00',
        dailyScheduleEnabled: scheduleData.enabled || false,
        organizationSpecificEnabled: orgSpecificData.enabled || false,
        selectedOrganizations: orgSpecificData.selectedOrganizations || [],
      });
    } catch (error) {
      console.error('Error loading AI follow-up settings:', error);
    }
  };

  const handleAiFollowupToggle = async (checked: boolean) => {
    try {
      setAiFollowupLoading(true);
      const newSettings = { ...aiFollowup, enabled: checked };
      setAiFollowup(newSettings);

      await setDoc(doc(db, 'settings', 'aiFollowup'), {
        enabled: checked,
        updatedAt: new Date()
      });

      toast.success(checked ? 'AI follow-up system enabled' : 'AI follow-up system disabled');
    } catch (error) {
      console.error('Error toggling AI follow-up:', error);
      toast.error('Failed to update AI follow-up settings');
    } finally {
      setAiFollowupLoading(false);
    }
  };

  const handleDailyScheduleToggle = async (checked: boolean) => {
    try {
      setAiFollowupLoading(true);
      const newSettings = { ...aiFollowup, dailyScheduleEnabled: checked };
      setAiFollowup(newSettings);
      
      await setDoc(doc(db, 'settings', 'dailySchedule'), {
        enabled: checked,
        followupTime: aiFollowup.followupTime,
        updatedAt: new Date()
      });
      
      toast.success(checked ? 'Daily scheduled followups enabled' : 'Daily scheduled followups disabled');
    } catch (error) {
      console.error('Error toggling daily schedule:', error);
      toast.error('Failed to update daily schedule settings');
    } finally {
      setAiFollowupLoading(false);
    }
  };

  const handleOrganizationSpecificToggle = async (checked: boolean) => {
    try {
      setAiFollowupLoading(true);
      const newSettings = { ...aiFollowup, organizationSpecificEnabled: checked };
      setAiFollowup(newSettings);
      
      await setDoc(doc(db, 'settings', 'organizationSpecificFollowup'), {
        enabled: checked,
        selectedOrganizations: aiFollowup.selectedOrganizations,
        updatedAt: new Date()
      });
      
      toast.success(checked ? 'Organization-specific daily followups enabled' : 'Organization-specific daily followups disabled');
    } catch (error) {
      console.error('Error toggling organization-specific followup:', error);
      toast.error('Failed to update organization-specific followup settings');
    } finally {
      setAiFollowupLoading(false);
    }
  };

  const handleAddOrganization = async (organizationId: string) => {
    if (!organizationId || aiFollowup.selectedOrganizations.includes(organizationId)) return;
    
    try {
      setAiFollowupLoading(true);
      const newSelectedOrgs = [...aiFollowup.selectedOrganizations, organizationId];
      const newSettings = { ...aiFollowup, selectedOrganizations: newSelectedOrgs };
      setAiFollowup(newSettings);
      
      await setDoc(doc(db, 'settings', 'organizationSpecificFollowup'), {
        enabled: aiFollowup.organizationSpecificEnabled,
        selectedOrganizations: newSelectedOrgs,
        updatedAt: new Date()
      });
      
      toast.success('Organization added to AI followup');
    } catch (error) {
      console.error('Error adding organization:', error);
      toast.error('Failed to add organization');
    } finally {
      setAiFollowupLoading(false);
    }
  };

  const handleRemoveOrganization = async (organizationId: string) => {
    try {
      setAiFollowupLoading(true);
      const newSelectedOrgs = aiFollowup.selectedOrganizations.filter(id => id !== organizationId);
      const newSettings = { ...aiFollowup, selectedOrganizations: newSelectedOrgs };
      setAiFollowup(newSettings);
      
      await setDoc(doc(db, 'settings', 'organizationSpecificFollowup'), {
        enabled: aiFollowup.organizationSpecificEnabled,
        selectedOrganizations: newSelectedOrgs,
        updatedAt: new Date()
      });
      
      toast.success('Organization removed from AI followup');
    } catch (error) {
      console.error('Error removing organization:', error);
      toast.error('Failed to remove organization');
    } finally {
      setAiFollowupLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setAiFollowupLoading(true);
      
      await setDoc(doc(db, 'settings', 'aiFollowup'), {
        enabled: aiFollowup.enabled,
        updatedAt: new Date()
      });

      await setDoc(doc(db, 'settings', 'dailySchedule'), {
        enabled: aiFollowup.dailyScheduleEnabled,
        followupTime: aiFollowup.followupTime,
        updatedAt: new Date()
      });
      
      toast.success(isRTL ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(isRTL ? 'فشل في حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setAiFollowupLoading(false);
    }
  };

  // AI Testing functions
  const searchUserByPhone = async () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setIsSearching(true);
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('phone', '==', phoneNumber.trim())
      );
      
      const querySnapshot = await getDocs(usersQuery);
      
      if (querySnapshot.empty) {
        toast.error('No user found with this phone number');
        setSelectedUser(null);
        return;
      }

      const userData = querySnapshot.docs[0].data();
      
      // Fetch user's habits
      const userHabitsQuery = query(
        collection(db, 'habits'),
        where('userId', '==', querySnapshot.docs[0].id)
      );
      const habitsSnapshot = await getDocs(userHabitsQuery);
      let habits = habitsSnapshot.docs
        .map(doc => ({
          id: doc.id,
          name: doc.data().name || 'Unnamed Habit',
          isActive: doc.data().isActive
        }))
        .filter(habit => habit.isActive !== false);

      const firstName = language === 'ar' ? userData.firstNameAr : userData.firstNameEn;
      const lastName = language === 'ar' ? userData.lastNameAr : userData.lastNameEn;
      const displayName = firstName ? (lastName ? `${firstName} ${lastName}` : firstName) : 'Unknown User';

      const user = {
        id: querySnapshot.docs[0].id,
        name: displayName,
        phone: userData.phone,
        email: userData.email,
        habits: habits.map(habit => ({
          id: habit.id,
          name: habit.name
        }))
      };
      
      setSelectedUser(user);
      toast.success('User found!');
    } catch (error) {
      console.error('Error searching for user:', error);
      toast.error('Error searching for user');
    } finally {
      setIsSearching(false);
    }
  };

  const startTestFollowup = async () => {
    if (!selectedUser) {
      toast.error('Please select a user first');
      return;
    }

    if (!selectedUser.habits || selectedUser.habits.length === 0) {
      toast.error('User has no active habits to follow up on');
      return;
    }

    setIsTestLoading(true);
    try {
      const response = await fetch('https://us-central1-namat-clinic.cloudfunctions.net/triggerUserFollowup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          phone: selectedUser.phone
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Test follow-up started successfully!');
        loadTestConversations();
      } else {
        throw new Error(result.error || 'Failed to start follow-up');
      }
    } catch (error: any) {
      console.error('Error starting test followup:', error);
      toast.error('Error starting test followup: ' + error.message);
    } finally {
      setIsTestLoading(false);
    }
  };

  const loadTestConversations = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const conversationsQuery = query(
        collection(db, 'habitFollowupConversations'),
        where('date', '==', today),
        where('isTest', '==', true)
      );
      
      const querySnapshot = await getDocs(conversationsQuery);
      const conversations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTestConversations(conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const deleteConversation = async (conversationId: string) => {
    setDeletingConversations(prev => new Set(prev).add(conversationId));
    try {
      await deleteDoc(doc(db, 'habitFollowupConversations', conversationId));
      toast.success('Conversation deleted');
      setTestConversations(prev => prev.filter(conv => conv.id !== conversationId));
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeletingConversations(prev => {
        const newSet = new Set(prev);
        newSet.delete(conversationId);
        return newSet;
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const copyConversationData = async (conversation: any) => {
    try {
      let output = `=== AI Test Conversation Details ===\n`;
      output += `Phone: ${conversation.phone}\n`;
      output += `Date: ${conversation.date}\n`;
      output += `Status: ${conversation.status}\n`;
      output += `Messages: ${conversation.messages?.length || 0}\n\n`;

      output += `Habits:\n`;
      conversation.habits?.forEach((habit: any, index: number) => {
        output += `${index + 1}. ${habit.habitName} - ${habit.completed ? 'Completed ✅' : 'Not Completed ❌'}\n`;
      });

      await navigator.clipboard.writeText(output);
      toast.success(isRTL ? 'تم نسخ تفاصيل المحادثة' : 'Conversation details copied');
    } catch (error) {
      console.error('Error copying conversation data:', error);
      toast.error(isRTL ? 'فشل في نسخ البيانات' : 'Failed to copy data');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden" dir={dir}>
          <DialogHeader>
            <DialogTitle className={isRTL ? 'text-right' : 'text-left'}>
              {isRTL ? 'إعدادات العادات' : 'Habits Settings'}
            </DialogTitle>
            <DialogDescription className={isRTL ? 'text-right' : 'text-left'}>
              {isRTL ? 'إدارة العادات المحددة مسبقاً وإعدادات المتابعة' : 'Manage preset habits and follow-up settings'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="presets" className="flex-1 flex flex-col overflow-hidden" dir={dir}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="presets">
                {isRTL ? 'العادات المحددة مسبقاً' : 'Preset Habits'}
              </TabsTrigger>
              <TabsTrigger value="followups">
                {isRTL ? 'متابعة العادات' : 'Habits Followups'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="presets" className="flex-1 overflow-y-auto mt-4">
              <HabitPresetManagement />
            </TabsContent>

            <TabsContent value="followups" className="flex-1 overflow-y-auto mt-4 space-y-6">
              {/* AI Follow-up Settings */}
              <div className="space-y-4">
                <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                  <h3 className="text-lg font-medium">
                    {isRTL ? 'نظام متابعة العادات بالذكاء الاصطناعي' : 'AI Habit Follow-up System'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {isRTL ? 'متابعة تلقائية مع المستخدمين حول عاداتهم اليومية عبر واتساب باستخدام الذكاء الاصطناعي' : 'Automatically follow up with customers about their daily habits via WhatsApp using AI'}
                  </p>
                </div>

                {/* AI Follow-up Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bot className="h-5 w-5 text-blue-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="ai-followup-enabled" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'تفعيل الذكاء الاصطناعي' : 'Enable AI'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {isRTL ? 'تفعيل نظام الذكاء الاصطناعي لمعالجة وتفسير الردود' : 'Enable AI system to process and interpret responses'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="ai-followup-enabled"
                    checked={aiFollowup.enabled}
                    onCheckedChange={handleAiFollowupToggle}
                    disabled={aiFollowupLoading}
                  />
                </div>

                {/* Daily Schedule Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="daily-schedule-enabled" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'تفعيل متابعة العادات اليومية' : 'Enable Daily Habit Follow-up'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {isRTL ? 'تفعيل إرسال رسائل المتابعة اليومية التلقائية لجميع المستخدمين' : 'Enable sending automatic daily follow-up messages to all users'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="daily-schedule-enabled"
                    checked={aiFollowup.dailyScheduleEnabled}
                    onCheckedChange={handleDailyScheduleToggle}
                    disabled={aiFollowupLoading}
                  />
                </div>

                {/* Organization-Specific Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-purple-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="organization-specific-enabled" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'تفعيل لمنظمات محددة فقط' : 'Enable for Specific Organizations Only'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {isRTL ? 'تفعيل إرسال رسائل المتابعة للمستخدمين المنتمين لمنظمات محددة فقط' : 'Only send follow-up messages to users in selected organizations'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="organization-specific-enabled"
                    checked={aiFollowup.organizationSpecificEnabled}
                    onCheckedChange={handleOrganizationSpecificToggle}
                    disabled={aiFollowupLoading}
                  />
                </div>

                {/* Organization Selector */}
                {aiFollowup.organizationSpecificEnabled && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600" />
                      <Label className="text-sm font-medium">
                        {isRTL ? 'المنظمات المحددة للمتابعة' : 'Organizations Selected for Follow-up'}
                      </Label>
                    </div>
                    
                    <Select 
                      onValueChange={handleAddOrganization}
                      disabled={aiFollowupLoading || organizationsLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isRTL ? 'اختر منظمة لإضافتها' : 'Select organization to add'} />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations
                          .filter(org => !aiFollowup.selectedOrganizations.includes(org.id))
                          .map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>

                    {aiFollowup.selectedOrganizations.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {aiFollowup.selectedOrganizations.map((orgId) => {
                          const org = organizations.find(o => o.id === orgId);
                          return (
                            <Badge key={orgId} variant="secondary" className="flex items-center gap-1 px-2 py-1">
                              <Users className="h-3 w-3" />
                              {org?.name || 'Unknown'}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-red-100"
                                onClick={() => handleRemoveOrganization(orgId)}
                                disabled={aiFollowupLoading}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <Separator />

                {/* AI provider key — deliberately NOT editable here. */}
                {/* This project's Firestore rules are world-readable, so a key
                    typed into this dialog would be public the moment it saved.
                    It lives in Secret Manager as ANTHROPIC_API_KEY instead. */}
                <div className="space-y-2">
                  <Label>{isRTL ? 'مفتاح Anthropic API' : 'Anthropic API Key'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {isRTL
                      ? 'يُدار كسر خادم (ANTHROPIC_API_KEY) ولا يمكن تعديله من هنا. تدويره يتطلب تحديث السر وإعادة نشر الدوال.'
                      : 'Managed as a server secret (ANTHROPIC_API_KEY); it cannot be edited here. Rotating it means updating the secret and redeploying the functions.'}
                  </p>
                </div>

                {/* Follow-up Time */}
                <div className="space-y-2">
                  <Label htmlFor="followup-time">
                    {isRTL ? 'وقت المتابعة اليومية' : 'Daily Follow-up Time'}
                  </Label>
                  <Select
                    value={aiFollowup.followupTime}
                    onValueChange={(value) => setAiFollowup({ ...aiFollowup, followupTime: value })}
                    disabled={aiFollowupLoading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hours.map((hour) => (
                        <SelectItem key={hour} value={hour}>
                          {hour}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={aiFollowupLoading}>
                    {aiFollowupLoading ? (
                      <>
                        <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {isRTL ? 'جاري الحفظ...' : 'Saving...'}
                      </>
                    ) : (
                      isRTL ? 'حفظ الإعدادات' : 'Save Settings'
                    )}
                  </Button>
                </div>

                <Separator />

                {/* AI Testing Section */}
                <div className="space-y-4">
                  <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      <Play className="h-5 w-5 text-blue-600" />
                      {isRTL ? 'اختبار رسائل الذكاء الاصطناعي' : 'AI Message Testing'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {isRTL ? 'اختبر محادثات متابعة الذكاء الاصطناعي فوراً مع مستخدمين محددين' : 'Test AI follow-up conversations instantly with specific users'}
                    </p>
                  </div>

                  {/* Test Interface */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Play className="h-5 w-5" />
                        {isRTL ? 'بدء محادثة اختبار' : 'Start Test Conversation'}
                      </CardTitle>
                      <CardDescription>
                        {isRTL ? 'أدخل رقم الهاتف للعثور على المستخدم وبدء محادثة اختبار' : 'Enter phone number to find user and start test conversation'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="test-phone">{isRTL ? 'رقم الهاتف' : 'Phone Number'}</Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Phone className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                            <Input
                              id="test-phone"
                              type="tel"
                              placeholder="05xxxxxxxx"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value)}
                              className={isRTL ? 'pr-10' : 'pl-10'}
                              disabled={isSearching}
                              dir="ltr"
                            />
                          </div>
                          <Button onClick={searchUserByPhone} disabled={isSearching || !phoneNumber.trim()} variant="outline">
                            {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? 'بحث' : 'Search')}
                          </Button>
                        </div>
                      </div>

                      {selectedUser && (
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-medium">{selectedUser.name}</p>
                              <p className="text-sm text-muted-foreground">{selectedUser.phone}</p>
                            </div>
                            <User className="h-8 w-8 text-muted-foreground" />
                          </div>
                          
                          {selectedUser.habits && selectedUser.habits.length > 0 && (
                            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                              <p className="text-sm font-medium mb-2">
                                {isRTL ? `عادات المستخدم (${selectedUser.habits.length})` : `User Habits (${selectedUser.habits.length})`}
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {selectedUser.habits.map((habit: any) => (
                                  <Badge key={habit.id} variant="secondary" className="text-xs">
                                    {habit.name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <Button
                            onClick={startTestFollowup}
                            disabled={isTestLoading || !selectedUser.habits || selectedUser.habits.length === 0}
                            className="w-full mt-3"
                          >
                            {isTestLoading ? (
                              <>
                                <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                {isRTL ? 'جاري البدء...' : 'Starting...'}
                              </>
                            ) : (
                              <>
                                <Play className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                {isRTL ? 'بدء المتابعة' : 'Start Follow-up'}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Active Test Conversations */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5" />
                        {isRTL ? 'المحادثات التجريبية النشطة' : 'Active Test Conversations'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {testConversations.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <MessageCircle className="mx-auto h-12 w-12 mb-4 opacity-50" />
                          <p>{isRTL ? 'لا توجد محادثات تجريبية نشطة' : 'No active test conversations'}</p>
                        </div>
                      ) : (
                        <ScrollArea className="max-h-[400px]">
                          <div className="space-y-3">
                            {testConversations.map((conversation) => (
                              <div key={conversation.id} className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                      {getStatusIcon(conversation.status)}
                                      <span className="font-medium">{conversation.userName}</span>
                                    </div>
                                    <Badge className={getStatusColor(conversation.status)}>
                                      {conversation.status}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">{conversation.phone}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyConversationData(conversation)}
                                      className="h-6 w-6 p-0"
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteConversation(conversation.id)}
                                      disabled={deletingConversations.has(conversation.id)}
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                    >
                                      {deletingConversations.has(conversation.id) ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <X className="h-3 w-3" />
                                      )}
                                    </Button>
                                  </div>
                                </div>
                                
                                {conversation.habits && conversation.habits.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {conversation.habits.map((habit: any, index: number) => (
                                      <Badge
                                        key={index}
                                        variant={habit.completed ? "default" : "secondary"}
                                        className="text-xs"
                                      >
                                        {habit.habitName}
                                        {habit.completed && <CheckCircle className={`h-3 w-3 ${isRTL ? 'mr-1' : 'ml-1'}`} />}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Debug Modal */}
      <Dialog open={debugModalOpen} onOpenChange={setDebugModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              {isRTL ? 'تفاصيل رسالة الذكاء الاصطناعي' : 'AI Message Details'}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {selectedMessage && (
              <pre className="text-xs whitespace-pre-wrap font-mono bg-muted p-4 rounded">
                {JSON.stringify(selectedMessage, null, 2)}
              </pre>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
